import { doodBlockConfig } from './dood-block-config';
import type { BlocCategory } from './bloc-categories';
import type {
  BlocAllowedIn,
  DefaultBlockDefinition,
  SlotDefinition,
} from './bloc-definition';

function createGenericBlockConfig(
  name: string,
  componentType: string,
  category: BlocCategory,
  options: {
    isRootBlock?: true;
    hideFromPicker?: true;
    defaultBlocks?: readonly DefaultBlockDefinition[];
    allowedIn?: readonly BlocAllowedIn[];
  } = {},
) {
  const slots: readonly SlotDefinition[] | undefined = options.defaultBlocks
    ? [
        {
          key: 'body',
          name: 'pages.blocks.common.body.label',
          accept: 'explicitOnly',
          allowedTypes: ['.*'],
          slotItemFormat: 'ids',
          defaultBlocks: options.defaultBlocks,
        },
      ]
    : undefined;

  return doodBlockConfig(
    {
      name,
      componentType,
      category,
      description: `${name}.`,
      keywords: [],
      sharedFields: {},
      ...(options.isRootBlock ? { isRootBlock: true } : {}),
      ...(options.hideFromPicker ? { hideFromPicker: true } : {}),
      ...(slots ? { slots } : {}),
      ...(options.allowedIn ? { allowedIn: options.allowedIn } : {}),
    },

    () => ({}),

    () => 'displayAll'
);
}

const prepaidAccountTopUpBlock = createGenericBlockConfig(
  'Prepaid account top-up',
  'PrepaidAccountTopUpBlockComponent',
  'prepaid',
  {
    allowedIn: [{ bloc: '*Page', slot: '*' }],
  },
);
export const prepaidAccountTopUpBlockDescriptor = prepaidAccountTopUpBlock.blockDescriptor;
export const prepaidAccountTopUpBlockBlocDefinition = prepaidAccountTopUpBlock.blocDefinition;
export const prepaidAccountTopUpBlockDashboardLayout = prepaidAccountTopUpBlock.dashboardLayout;
export const prepaidAccountTopUpBlockProperties = prepaidAccountTopUpBlock.properties;
export const prepaidAccountTopUpBlockConfig = prepaidAccountTopUpBlock.config;
export const prepaidAccountTopUpBlockResolveConfig = prepaidAccountTopUpBlock.resolveConfig;
export type PrepaidAccountTopUpBlockConfig = typeof prepaidAccountTopUpBlockConfig;

const validateSharedOrderHeaderBlock = createGenericBlockConfig(
  'Validate shared order header',
  'ValidateSharedOrderHeaderBlockComponent',
  'content',
  { hideFromPicker: true },
);
export const validateSharedOrderHeaderBlockDescriptor = validateSharedOrderHeaderBlock.blockDescriptor;
export const validateSharedOrderHeaderBlockBlocDefinition = validateSharedOrderHeaderBlock.blocDefinition;
export const validateSharedOrderHeaderBlockDashboardLayout = validateSharedOrderHeaderBlock.dashboardLayout;
export const validateSharedOrderHeaderBlockProperties = validateSharedOrderHeaderBlock.properties;
export const validateSharedOrderHeaderBlockConfig = validateSharedOrderHeaderBlock.config;
export const validateSharedOrderHeaderBlockResolveConfig = validateSharedOrderHeaderBlock.resolveConfig;
export type ValidateSharedOrderHeaderBlockConfig = typeof validateSharedOrderHeaderBlockConfig;

const validateSharedOrderBodyBlock = createGenericBlockConfig(
  'Validate shared order body',
  'ValidateSharedOrderBodyBlockComponent',
  'content',
  { hideFromPicker: true },
);
export const validateSharedOrderBodyBlockDescriptor = validateSharedOrderBodyBlock.blockDescriptor;
export const validateSharedOrderBodyBlockBlocDefinition = validateSharedOrderBodyBlock.blocDefinition;
export const validateSharedOrderBodyBlockDashboardLayout = validateSharedOrderBodyBlock.dashboardLayout;
export const validateSharedOrderBodyBlockProperties = validateSharedOrderBodyBlock.properties;
export const validateSharedOrderBodyBlockConfig = validateSharedOrderBodyBlock.config;
export const validateSharedOrderBodyBlockResolveConfig = validateSharedOrderBodyBlock.resolveConfig;
export type ValidateSharedOrderBodyBlockConfig = typeof validateSharedOrderBodyBlockConfig;

const validateSharedOrderPage = createGenericBlockConfig(
  'Validate shared order page',
  'ValidateSharedOrderPageComponent',
  'page',
  {
    isRootBlock: true,
    defaultBlocks: [
      { id: 'validate-shared-order-header-block', type: 'ValidateSharedOrderHeaderBlockComponent' },
      { id: 'validate-shared-order-body-block', type: 'ValidateSharedOrderBodyBlockComponent' },
    ],
  },
);
export const validateSharedOrderPageDescriptor = validateSharedOrderPage.blockDescriptor;
export const validateSharedOrderPageBlocDefinition = validateSharedOrderPage.blocDefinition;
export const validateSharedOrderPageDashboardLayout = validateSharedOrderPage.dashboardLayout;
export const validateSharedOrderPageProperties = validateSharedOrderPage.properties;
export const validateSharedOrderPageConfig = validateSharedOrderPage.config;
export const validateSharedOrderPageResolveConfig = validateSharedOrderPage.resolveConfig;
export type ValidateSharedOrderPageConfig = typeof validateSharedOrderPageConfig;

const paymentConfirmPage = createGenericBlockConfig(
  'Payment confirmation page',
  'PaymentConfirmPageComponent',
  'page',
  {
    isRootBlock: true,
    defaultBlocks: [{ id: 'payment-header-block', type: 'PaymentPageHeaderBlockComponent' }],
  },
);
export const paymentConfirmPageDescriptor = paymentConfirmPage.blockDescriptor;
export const paymentConfirmPageBlocDefinition = paymentConfirmPage.blocDefinition;
export const paymentConfirmPageDashboardLayout = paymentConfirmPage.dashboardLayout;
export const paymentConfirmPageProperties = paymentConfirmPage.properties;
export const paymentConfirmPageConfig = paymentConfirmPage.config;
export const paymentConfirmPageResolveConfig = paymentConfirmPage.resolveConfig;
export type PaymentConfirmPageConfig = typeof paymentConfirmPageConfig;

const groupPaymentPageHeaderBlock = createGenericBlockConfig(
  'Group payment page header',
  'GroupPaymentPageHeaderBlockComponent',
  'content',
  { hideFromPicker: true },
);
export const groupPaymentPageHeaderBlockDescriptor = groupPaymentPageHeaderBlock.blockDescriptor;
export const groupPaymentPageHeaderBlockBlocDefinition = groupPaymentPageHeaderBlock.blocDefinition;
export const groupPaymentPageHeaderBlockDashboardLayout = groupPaymentPageHeaderBlock.dashboardLayout;
export const groupPaymentPageHeaderBlockProperties = groupPaymentPageHeaderBlock.properties;
export const groupPaymentPageHeaderBlockConfig = groupPaymentPageHeaderBlock.config;
export const groupPaymentPageHeaderBlockResolveConfig = groupPaymentPageHeaderBlock.resolveConfig;
export type GroupPaymentPageHeaderBlockConfig = typeof groupPaymentPageHeaderBlockConfig;

const groupPaymentListBlock = createGenericBlockConfig(
  'Group payment list',
  'GroupPaymentListBlockComponent',
  'content',
  { hideFromPicker: true },
);
export const groupPaymentListBlockDescriptor = groupPaymentListBlock.blockDescriptor;
export const groupPaymentListBlockBlocDefinition = groupPaymentListBlock.blocDefinition;
export const groupPaymentListBlockDashboardLayout = groupPaymentListBlock.dashboardLayout;
export const groupPaymentListBlockProperties = groupPaymentListBlock.properties;
export const groupPaymentListBlockConfig = groupPaymentListBlock.config;
export const groupPaymentListBlockResolveConfig = groupPaymentListBlock.resolveConfig;
export type GroupPaymentListBlockConfig = typeof groupPaymentListBlockConfig;

const groupPaymentPage = createGenericBlockConfig(
  'Group payment page',
  'GroupPaymentPageComponent',
  'page',
  {
    isRootBlock: true,
    defaultBlocks: [
      { id: 'group-payment-header-block', type: 'GroupPaymentPageHeaderBlockComponent' },
      { id: 'group-payment-list-block', type: 'GroupPaymentListBlockComponent' },
    ],
  },
);
export const groupPaymentPageDescriptor = groupPaymentPage.blockDescriptor;
export const groupPaymentPageBlocDefinition = groupPaymentPage.blocDefinition;
export const groupPaymentPageDashboardLayout = groupPaymentPage.dashboardLayout;
export const groupPaymentPageProperties = groupPaymentPage.properties;
export const groupPaymentPageConfig = groupPaymentPage.config;
export const groupPaymentPageResolveConfig = groupPaymentPage.resolveConfig;
export type GroupPaymentPageConfig = typeof groupPaymentPageConfig;

const checkDetailsPage = createGenericBlockConfig(
  'Check details page',
  'CheckDetailsPageComponent',
  'page',
  {
    isRootBlock: true,
    defaultBlocks: [
      { id: 'header-block', type: 'HeaderBlockComponent' },
      { id: 'check-details-block', type: 'CheckDetailsBlockComponent' },
    ],
  },
);
export const checkDetailsPageDescriptor = checkDetailsPage.blockDescriptor;
export const checkDetailsPageBlocDefinition = checkDetailsPage.blocDefinition;
export const checkDetailsPageDashboardLayout = checkDetailsPage.dashboardLayout;
export const checkDetailsPageProperties = checkDetailsPage.properties;
export const checkDetailsPageConfig = checkDetailsPage.config;
export const checkDetailsPageResolveConfig = checkDetailsPage.resolveConfig;
export type CheckDetailsPageConfig = typeof checkDetailsPageConfig;

const patPage = createGenericBlockConfig(
  'Pay at table page',
  'PatPageComponent',
  'pay_at_table',
  { isRootBlock: true },
);
export const patPageDescriptor = patPage.blockDescriptor;
export const patPageBlocDefinition = patPage.blocDefinition;
export const patPageDashboardLayout = patPage.dashboardLayout;
export const patPageProperties = patPage.properties;
export const patPageConfig = patPage.config;
export const patPageResolveConfig = patPage.resolveConfig;
export type PatPageConfig = typeof patPageConfig;

const patPaymentPage = createGenericBlockConfig(
  'Pay at table payment page',
  'PatPaymentPageComponent',
  'pay_at_table',
  { isRootBlock: true },
);
export const patPaymentPageDescriptor = patPaymentPage.blockDescriptor;
export const patPaymentPageBlocDefinition = patPaymentPage.blocDefinition;
export const patPaymentPageDashboardLayout = patPaymentPage.dashboardLayout;
export const patPaymentPageProperties = patPaymentPage.properties;
export const patPaymentPageConfig = patPaymentPage.config;
export const patPaymentPageResolveConfig = patPaymentPage.resolveConfig;
export type PatPaymentPageConfig = typeof patPaymentPageConfig;

const accountPageBlock = createGenericBlockConfig(
  'Account',
  'AccountPageBlockComponent',
  'content',
  {
    allowedIn: [{ bloc: 'AccountPageComponent', slot: 'body' }],
  },
);
export const accountPageBlockDescriptor = accountPageBlock.blockDescriptor;
export const accountPageBlockBlocDefinition = accountPageBlock.blocDefinition;
export const accountPageBlockDashboardLayout = accountPageBlock.dashboardLayout;
export const accountPageBlockProperties = accountPageBlock.properties;
export const accountPageBlockConfig = accountPageBlock.config;
export const accountPageBlockResolveConfig = accountPageBlock.resolveConfig;
export type AccountPageBlockConfig = typeof accountPageBlockConfig;

const checksPage = createGenericBlockConfig('Checks page', 'ChecksPageComponent', 'page', {
  isRootBlock: true,
  defaultBlocks: [
    { id: 'header-block', type: 'HeaderBlockComponent' },
    { id: 'checks-block', type: 'ChecksPageBlockComponent' },
  ],
});
export const checksPageDescriptor = checksPage.blockDescriptor;
export const checksPageBlocDefinition = checksPage.blocDefinition;
export const checksPageDashboardLayout = checksPage.dashboardLayout;
export const checksPageProperties = checksPage.properties;
export const checksPageConfig = checksPage.config;
export const checksPageResolveConfig = checksPage.resolveConfig;
export type ChecksPageConfig = typeof checksPageConfig;

const debugNetworkConnectionBlock = createGenericBlockConfig(
  'Debug network connection',
  'DebugNetworkConnectionBlockComponent',
  'content',
  { hideFromPicker: true },
);
export const debugNetworkConnectionBlockDescriptor = debugNetworkConnectionBlock.blockDescriptor;
export const debugNetworkConnectionBlockBlocDefinition = debugNetworkConnectionBlock.blocDefinition;
export const debugNetworkConnectionBlockDashboardLayout = debugNetworkConnectionBlock.dashboardLayout;
export const debugNetworkConnectionBlockProperties = debugNetworkConnectionBlock.properties;
export const debugNetworkConnectionBlockConfig = debugNetworkConnectionBlock.config;
export const debugNetworkConnectionBlockResolveConfig = debugNetworkConnectionBlock.resolveConfig;
export type DebugNetworkConnectionBlockConfig = typeof debugNetworkConnectionBlockConfig;
